import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, GitBranch, Users, UsersRound, UserCog, Inbox,
  GraduationCap, LogOut, Settings as SettingsIcon, MoreHorizontal, X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { GlobalHarvestLogo, JsirLogo } from "./Brand";

// Mobile bottom-tab nav (5 most-used).
const PRIMARY_NAV = [
  { to: "/dashboard", label: "Dashboard", short: "Home",   icon: LayoutDashboard },
  { to: "/pathway",   label: "Pathway",   short: "Pathway",icon: GitBranch },
  { to: "/people",    label: "People",    short: "People", icon: Users },
  { to: "/leads",     label: "Leads",     short: "Leads",  icon: Inbox },
  { to: "/leaders",   label: "Leaders",   short: "Leaders",icon: UserCog },
];

// Desktop top-nav (a couple of extras the mobile bottom-tab can't fit).
const DESKTOP_EXTRA_NAV = [
  { to: "/groups",    label: "Groups",    icon: UsersRound },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const signOut = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* ===== Top header (always visible) ===== */}
      <header className="bg-white border-b border-ink-200 sticky top-0 z-40 pt-safe">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 shrink-0 min-w-0">
            <JsirLogo className="h-8 md:h-9 w-auto" />
            <div className="hidden md:flex items-center gap-2 text-xs text-ink-500 pl-3 border-l border-ink-200">
              <span>powered by</span>
              <GlobalHarvestLogo className="h-5 w-auto" />
            </div>
          </div>

          {/* Desktop primary nav */}
          <nav className="hidden md:flex items-center gap-1">
            {[...PRIMARY_NAV, ...DESKTOP_EXTRA_NAV].map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden lg:inline">{n.label}</span>
                </NavLink>
              );
            })}
            <NavLink
              to="/training"
              className={({ isActive }) => `btn-primary ml-2 ${isActive ? "ring-2 ring-brand-700" : ""}`}
            >
              <GraduationCap className="h-4 w-4" />
              <span className="hidden lg:inline">Training Hub</span>
            </NavLink>
          </nav>

          {/* Right side: name+org (desktop), settings (desktop), menu button (mobile) */}
          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            <NavLink to="/settings" className="text-right text-sm hidden sm:block hover:opacity-80 transition" title="Workspace settings">
              <div className="font-medium text-ink-900 leading-tight truncate max-w-[160px]">{user?.name}</div>
              <div className="text-ink-500 text-xs truncate max-w-[160px]">{user?.org_name}</div>
            </NavLink>
            <NavLink
              to="/settings"
              className={({ isActive }) => `btn-ghost hidden md:inline-flex ${isActive ? "text-brand bg-brand-50" : ""}`}
              title="Workspace settings"
            >
              <SettingsIcon className="h-4 w-4" />
            </NavLink>
            <button
              onClick={signOut}
              className="btn-ghost hidden md:inline-flex"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
            {/* Mobile menu trigger */}
            <button
              onClick={() => setMenuOpen(true)}
              className="btn-ghost md:hidden"
              title="More"
              aria-label="More"
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ===== Main content ===== */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 py-5 md:py-8 has-bottom-nav md:pb-8">
        <Outlet />
      </main>

      {/* Footer (desktop only — mobile has bottom nav instead) */}
      <footer className="hidden md:block text-center text-xs text-ink-400 py-6">
        © {new Date().getFullYear()} Jesus Said in Red · DBS Platform
      </footer>

      {/* ===== Bottom tab bar (mobile only) ===== */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-ink-200 pb-safe"
        aria-label="Primary"
      >
        <div className="flex items-stretch">
          {PRIMARY_NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => `tab-link ${isActive ? "tab-link-active" : ""}`}
              >
                <Icon className="h-5 w-5" />
                <span>{n.short}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* ===== Mobile menu sheet ===== */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col">
          <div className="flex-1 bg-ink-900/40 animate-fade-in" onClick={() => setMenuOpen(false)} />
          <div className="bg-white rounded-t-2xl shadow-2xl pb-safe animate-sheet-up">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <div>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs text-ink-500">{user?.org_name}</div>
              </div>
              <button onClick={() => setMenuOpen(false)} className="btn-ghost p-2" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="border-t border-ink-100 py-2">
              <SheetItem to="/training" icon={GraduationCap} label="Training Hub" onClick={() => setMenuOpen(false)} accent />
              <SheetItem to="/groups"   icon={UsersRound}    label="Groups"        onClick={() => setMenuOpen(false)} />
              <SheetItem to="/settings" icon={SettingsIcon}  label="Workspace Settings" onClick={() => setMenuOpen(false)} />
              <button
                onClick={() => { setMenuOpen(false); signOut(); }}
                className="w-full flex items-center gap-3 px-5 py-3.5 text-left text-ink-700 active:bg-ink-50"
              >
                <LogOut className="h-5 w-5 text-ink-500" />
                <span>Sign out</span>
              </button>
            </div>
            <div className="px-5 py-3 text-center text-[11px] text-ink-400 border-t border-ink-100 flex items-center justify-center gap-2">
              <span>powered by</span> <GlobalHarvestLogo className="h-4 w-auto" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SheetItem({
  to, icon: Icon, label, onClick, accent,
}: { to: string; icon: any; label: string; onClick: () => void; accent?: boolean }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `w-full flex items-center gap-3 px-5 py-3.5 text-left active:bg-ink-50 ${
          accent ? "text-brand font-medium" : "text-ink-700"
        } ${isActive ? "bg-brand-50" : ""}`
      }
    >
      <Icon className={`h-5 w-5 ${accent ? "text-brand" : "text-ink-500"}`} />
      <span>{label}</span>
    </NavLink>
  );
}
